import { apiGet, apiPatch, apiPost, apiSend } from "./http";
import { EstimateStatus, Media, MediaEstimate, MediaHorizon, MediaLandmarks, MediaPeak, MediaPlace, MediaRelief, MediaSightings, MediaTerrain, NearestPeak, PhotoLocation, RandomPlace, RegionMapData } from "../../types";

export const getMedia = (mediaId: number) =>
  apiGet<Media>(`/media/${mediaId}`);

export const getRandomMediaWithPeak = () =>
  apiGet<Media>(`/media/random-with-peak`);

/**
 * A photo to open the view of a random place at (/place/:id), places of more photos more often;
 * not the place of `exceptMediaId` (the one shown) while there's another.
 */
export const getRandomPlace = (exceptMediaId?: number) =>
  apiGet<RandomPlace>(`/media/random-place${exceptMediaId != null ? `?except=${exceptMediaId}` : ''}`);

export const getMediaPeaks = (mediaId: number) =>
  apiGet<MediaPeak[]>(`/media/${mediaId}/peaks`);

export const getMedias = () =>
  apiGet(`/media`);

export const getMediasNoCoordinates = () =>
  apiGet(`/media/no-coordinates`);

export const updateMediaLocation = (mediaId: number, latitude: number, longitude: number) =>
  apiPatch<Media>(`/media/${mediaId}/location`, { latitude, longitude });

/** Puts several of the user's photos at one place. */
export const updateMediasLocation = (mediaIds: number[], latitude: number, longitude: number) =>
  apiPatch<Media[]>(`/media/location`, { ids: mediaIds, latitude, longitude });

/** Deletes the user's photos for good, with their tags, crops and files. All of them or none (403/404). */
export const deleteMedias = (mediaIds: number[]) =>
  apiPost<{ deleted: number[] }>(`/media/delete`, { ids: mediaIds });

/** Queues the peak estimates of the user's photos again; photos without a location get none (status null). */
export const queueMediaEstimates = (mediaIds: number[]) =>
  apiPost<{ queued: number; statuses: Record<string, EstimateStatus | null> }>(`/media/estimate`, { ids: mediaIds });

/** The photo's background peak estimate, for its owner. ApiError 422 when the photo has no location. */
export const getMediaEstimate = (mediaId: number): Promise<MediaEstimate> =>
  apiSend<MediaEstimate>("GET", `/media/${mediaId}/estimate`);

export const getMediaNearestPeak = (mediaId: number) =>
  apiGet<NearestPeak | null>(`/media/${mediaId}/nearest-peak`);

export const getPhotoLocations = () =>
  apiGet<PhotoLocation[]>(`/media/locations`);

/**
 * The spots other photos were taken from that can be seen from where this one was, with the
 * directions of its tags to fit the camera to (utils/sightings). Empty lists when the photo
 * has no GPS or too few tags.
 */
export const getMediaSightings = (mediaId: number) =>
  apiGet<MediaSightings>(`/media/${mediaId}/sightings`);

/** The photos taken at the same place as this one, with their tags' directions, to put together as one view. */
export const getMediaPlace = (mediaId: number) =>
  apiGet<MediaPlace>(`/media/${mediaId}/place`);

/** The cities and towns that can be seen from where the photo was taken, for their names over the panorama of the place. */
export const getMediaLandmarks = (mediaId: number) =>
  apiGet<MediaLandmarks>(`/media/${mediaId}/landmarks`);

export const getMediasNoSignedPeaks = () =>
  apiGet(`/media/no-signed-peaks`);

/** Saves the photo's tags: only the points, the backend frames the crops (PeakCropFrame). */
export const saveMediaPeaks = async (mediaId: number, mediaPeaks: MediaPeak[]): Promise<MediaPeak[]> => {
  const payload = {
    peaks: mediaPeaks.map(p => ({
      peakId: p.peak.id,
      xCoord: p.xCoord,
      yCoord: p.yCoord,
    })),
  };

  return apiPost<MediaPeak[]>(`/media/${mediaId}/peaks`, payload);
};

/**
 * Serves the owner's hand-edited peak crop. rawCropPath is the raw crop the image was
 * rendered from - the backend refuses it (409) if the frame has changed since.
 */
export const saveManualCrop = (
  mediaId: number,
  peakId: number,
  image: Blob,
  params: Record<string, number>,
  rawCropPath: string
): Promise<MediaPeak[]> => {
  const form = new FormData();
  form.append("image", image, "crop.jpg");
  form.append("params", JSON.stringify(params));
  form.append("rawCropPath", rawCropPath);
  return apiSend<MediaPeak[]>("POST", `/media/${mediaId}/peaks/${peakId}/manual-crop`, form);
};

/** Back to the automatically enhanced crop. */
export const resetManualCrop = (mediaId: number, peakId: number): Promise<MediaPeak[]> =>
  apiSend<MediaPeak[]>("DELETE", `/media/${mediaId}/peaks/${peakId}/manual-crop`);

/**
 * The terrain model of the photo's view, for suggesting peaks while its owner tags them: the
 * peaks seen in the sector from leftboundDeg clockwise to rightboundDeg, with the skyline
 * and mountain outlines. peakIds (tags not saved yet) come back wherever they are. Throws
 * ApiError: 422 when the photo has no GPS or there's no terrain data there, 503 when the
 * terrain service is down.
 *
 * The bounds go to 0.1° rounded outwards: the sector comes back as asked for, and has to
 * cover the one wanted (rounded to the nearest, the wanted one could end up to 0.05° outside
 * it, and useTerrainModel asked for it again and again).
 */
const sectorParams = (sector: { leftboundDeg: number; rightboundDeg: number }) => {
  const left = Math.floor(sector.leftboundDeg * 10) / 10;
  const span = (((sector.rightboundDeg - sector.leftboundDeg) % 360) + 360) % 360 || 360;
  // nearly all around: all around (rightbound = leftbound)
  const right = span >= 359.8 ? left : Math.ceil(sector.rightboundDeg * 10) / 10;
  return new URLSearchParams({
    leftboundDeg: left.toFixed(1),
    rightboundDeg: right.toFixed(1),
  });
};

export const getMediaTerrain = (
  mediaId: number,
  sector: { leftboundDeg: number; rightboundDeg: number },
  peakIds: number[],
): Promise<MediaTerrain> => {
  const params = sectorParams(sector);
  if (peakIds.length > 0) params.set("peakIds", peakIds.join(","));
  return apiSend<MediaTerrain>("GET", `/media/${mediaId}/terrain?${params}`);
};

/**
 * The ridges seen in a sector of the photo's view, for anyone: as getMediaTerrain, but with only
 * the tagged peaks (to fit the camera to), the sector at most 150° wide. Errors as there.
 */
export const getMediaRidges = (
  mediaId: number,
  sector: { leftboundDeg: number; rightboundDeg: number },
): Promise<MediaTerrain> => apiSend<MediaTerrain>("GET", `/media/${mediaId}/ridges?${sectorParams(sector)}`);

/**
 * The skyline and mountain outlines all around where the photo was taken, for fitting the
 * terrain model to a photo without tags (utils/blindFit). Owner only; errors as getMediaTerrain.
 */
export const getMediaHorizon = (mediaId: number): Promise<MediaHorizon> =>
  apiSend<MediaHorizon>("GET", `/media/${mediaId}/horizon`);

/** As getMediaHorizon, for anyone: the ridges all around, for the panorama of the place. Errors as getMediaRidges. */
export const getMediaRidgesAround = (mediaId: number): Promise<MediaHorizon> =>
  apiSend<MediaHorizon>("GET", `/media/${mediaId}/ridges/around`);

/** Which region each direction looks at from where the photo was taken, for the panorama of the place. */
export const getMediaRegionMap = (mediaId: number) =>
  apiGet<RegionMapData>(`/media/${mediaId}/regions/around`);

/** The terrain all around where the photo was taken, painted, for the panorama of the place to show between its photos. */
export const getMediaRelief = (mediaId: number) =>
  apiGet<MediaRelief>(`/media/${mediaId}/relief`);
